import React, { useState, useEffect, useRef, useCallback } from 'react';

// ============================================================
// CONFIGURACIÓN SUPABASE - Autenticación en CRM
// ============================================================
const SUPABASE_AUTH_URL = 'https://zkznupgyaorojxpydlly.supabase.co';
const SUPABASE_AUTH_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY';

// Datos de costes en Marketing
const SUPABASE_DATA_URL = process.env.REACT_APP_SUPABASE_URL || 'https://vgraqyqwrdgsqdkpltou.supabase.co';
const SUPABASE_DATA_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZncmFxeXF3cmRnc3Fka3BsdG91Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjcwMjgyMjIsImV4cCI6MjA4MjYwNDIyMn0.PQ9BV4f3s_wOoR4YKkeP0C5yW1pLXF-6CD7174Dvebc';

// ============================================================
// SUPABASE AUTH
// ============================================================
const supabaseAuth = {
  signIn: async (email, password) => {
    try {
      const response = await fetch(`${SUPABASE_AUTH_URL}/auth/v1/token?grant_type=password`, {
        method: 'POST',
        headers: { 'apikey': SUPABASE_AUTH_KEY, 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (!response.ok) {
        const error = await response.json();
        return { data: null, error: error.error_description || 'Credenciales incorrectas' };
      }
      const data = await response.json();
      localStorage.setItem('costes-access-token', data.access_token);
      localStorage.setItem('costes-refresh-token', data.refresh_token);
      localStorage.setItem('costes-user-email', email);
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  },
  signOut: () => {
    localStorage.removeItem('costes-access-token');
    localStorage.removeItem('costes-refresh-token');
    localStorage.removeItem('costes-user-email');
  },
  getCurrentUser: () => {
    const token = localStorage.getItem('costes-access-token');
    const email = localStorage.getItem('costes-user-email');
    if (token && email) return { email };
    return null;
  }
};

// ============================================================
// FETCH SUPABASE DATA
// ============================================================
const fetchSupabase = async (table, params = '') => {
  try {
    const response = await fetch(`${SUPABASE_DATA_URL}/rest/v1/${table}${params ? '?' + params : ''}`, {
      headers: {
        'apikey': SUPABASE_DATA_KEY,
        'Authorization': `Bearer ${SUPABASE_DATA_KEY}`,
        'Content-Type': 'application/json'
      }
    });
    if (!response.ok) throw new Error(`Error ${response.status}`);
    return await response.json();
  } catch (e) {
    console.error(`Error fetching ${table}:`, e);
    return [];
  }
};

// ============================================================
// COLORES
// ============================================================
const COLORS = {
  primary: '#005187',
  primaryLight: '#4d82bc',
  secondary: '#2563EB',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  gray: '#6B7280',
  lightGray: '#E5E7EB',
  background: '#f3f4f6',
  white: '#FFFFFF',
  dark: '#1F2937'
};

const HUB_COLORS = {
  'hub-marketing': '#005187',
  'hub-crm': '#2563EB',
  'centro-apps': '#059669'
};

const SERVERS_VERSION = 7; // bump to force localStorage reset on structure changes

const SERVER_DEFAULTS = [
  {
    id: 'server1',
    hetzner_id: 123745657,
    nombre: 'Servidor 1 - Backend',
    plan: 'CPX22',
    ip: '178.104.47.103',
    ram: '4 GB',
    cpu: '2 vCPU',
    disco: '80 GB SSD',
    coste_mensual: '9.67',
    proveedor: 'Hetzner Cloud',
    ubicacion: 'Nuremberg, DE',
    coolify_url: 'http://178.104.47.103:8000',
    apps: [
      { nombre: 'n8n-dimoauto', url: 'n8n.dimoauto.com', estado: true, subApps: ['Email Leads', 'Customer Leads', 'Meta Leads', 'Autoleads', 'Agente CRM Comercial', 'Agente CRM Admin', 'Métricas por plataforma', 'Embudo SEM y Panel', 'Agente Marketing', 'Task Runners'] },
      { nombre: 'c360-backendvision', url: 'c360-backend.dimoauto.com', estado: true, subApps: ['Playwright', 'Vision API', 'Stellantis ADFS'] },
      { nombre: 'meta-leads-bot', url: 'meta-bot.dimoauto.com', estado: true, subApps: ['Bot Meta Leads'] },
      { nombre: 'hetzner-mcp-backend', url: 'tlfjhjxagus59gysrsttxykq.178.104.47.103.sslip.io', estado: true, subApps: ['MCP Backend'] },
      { nombre: 'rotulador-dimoauto', url: 'alysl1m8xtyey5kqp49hmffp.178.104.47.103.sslip.io', estado: true, subApps: ['Rotulación de vídeos', 'ffmpeg', 'ImageMagick', 'faster-whisper'] },
    ]
  },
  {
    id: 'server2',
    hetzner_id: 125555742,
    nombre: 'Servidor 2 - Frontend',
    plan: 'CPX22',
    ip: '204.168.215.119',
    ram: '4 GB',
    cpu: '2 vCPU',
    disco: '80 GB SSD',
    coste_mensual: '9.67',
    proveedor: 'Hetzner Cloud',
    ubicacion: 'Helsinki, FI',
    coolify_url: 'http://204.168.215.119:8000',
    apps: [
      { nombre: 'centro-de-aplicaciones', url: 'app.dimoauto.com', estado: true, subApps: ['Launcher', 'Drive'] },
      { nombre: 'costes-app', url: 'costes.dimoauto.com', estado: true, subApps: ['Costes'] },
      { nombre: 'crm-comerciales', url: 'crm.dimoauto.com', estado: true, subApps: ['CRM Comerciales'] },
      { nombre: 'hub-crm', url: 'crmadmin.dimoauto.com', estado: true, subApps: ['CRM Admin', 'CRM Comerciales', 'Autoleads', 'Customer leads', 'Meta leads', 'Email leads'] },
      { nombre: 'hub-marketing', url: 'marketing.dimoauto.com', estado: true, subApps: ['Campañas', 'Panel de marketing'] },
      { nombre: 'MCP Claude-Cowork', url: 'mcp.dimoauto.com', estado: true, subApps: ['MCP Server'] },
      { nombre: 'meta-leads-live', url: 'meta-live.dimoauto.com', estado: true, subApps: ['Meta Leads Live'] },
    ]
  }
];

// ============================================================
// SERVER CARD
// ============================================================
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 GB';
  const gb = bytes / 1073741824;
  return gb >= 1 ? `${gb.toFixed(2)} GB` : `${(bytes / 1048576).toFixed(0)} MB`;
};

const ServerCard = ({ server, onSave, liveMetrics }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(server);
  const [newApp, setNewApp] = useState({ nombre: '', url: '', subApps: [] });
  const [addingApp, setAddingApp] = useState(false);
  const [expandedApps, setExpandedApps] = useState({});

  const save = () => { onSave(draft); setEditing(false); };
  const cancel = () => { setDraft(server); setEditing(false); setAddingApp(false); };
  const addApp = () => {
    if (!newApp.nombre.trim()) return;
    setDraft(d => ({ ...d, apps: [...d.apps, { ...newApp, estado: true }] }));
    setNewApp({ nombre: '', url: '', subApps: [] });
    setAddingApp(false);
  };
  const removeApp = (i) => setDraft(d => ({ ...d, apps: d.apps.filter((_, idx) => idx !== i) }));
  const toggleAppStatus = (i) => setDraft(d => ({ ...d, apps: d.apps.map((a, idx) => idx === i ? { ...a, estado: !a.estado } : a) }));
  const toggleExpand = (i) => setExpandedApps(p => ({ ...p, [i]: !p[i] }));

  const fieldStyle = {
    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: 6, padding: '5px 10px', color: 'white', fontSize: 13,
    outline: 'none', width: '100%', boxSizing: 'border-box'
  };

  const cpu = liveMetrics?.cpu_usage;
  const trafficOut = liveMetrics?.outgoing_traffic;
  const trafficIncluded = liveMetrics?.included_traffic || 21474836480; // 20TB default
  const trafficPct = trafficOut ? Math.min((trafficOut / trafficIncluded) * 100, 100) : 0;
  const isLive = liveMetrics && !liveMetrics.error;

  const totalApps = server.apps.reduce((sum, a) => sum + 1 + (a.subApps?.length || 0), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, height: '100%' }}>
      {/* Card */}
      <div style={{
        width: '100%', borderRadius: 16, overflow: 'hidden',
        background: 'linear-gradient(145deg, #0f1e35 0%, #0a1525 60%, #071020 100%)',
        border: '1px solid rgba(77,130,188,0.35)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
        flex: 1, display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ height: 3, background: 'linear-gradient(90deg, #2563eb, #4d82bc, #84b6f4, #4d82bc, #2563eb)' }} />

        {/* Header */}
        <div style={{ padding: '22px 20px 14px', borderBottom: '1px solid rgba(77,130,188,0.15)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editing
                ? <input value={draft.nombre} onChange={e => setDraft(d => ({ ...d, nombre: e.target.value }))} style={{ ...fieldStyle, fontSize: 16, fontWeight: 700, marginBottom: 4 }} />
                : <div onClick={() => setEditing(true)} title="Clic para editar" style={{ fontSize: 18, fontWeight: 800, color: '#e8f1ff', letterSpacing: '-0.3px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}>{server.nombre}</div>
              }
              {editing
                ? <input value={draft.ubicacion} onChange={e => setDraft(d => ({ ...d, ubicacion: e.target.value }))} style={{ ...fieldStyle, marginTop: 4 }} placeholder="Ubicación" />
                : <div style={{ fontSize: 12, color: '#6b9fd4', marginTop: 3 }}>{server.proveedor} · {server.ubicacion}</div>
              }
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
              {editing ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={save} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white' }}>✓ Guardar</button>
                  <button onClick={cancel} style={{ padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 12, background: 'rgba(255,255,255,0.08)', color: '#9ca3af' }}>✕</button>
                </div>
              ) : (
                <div style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 8, padding: '6px 12px', textAlign: 'right' }}>
                  <div style={{ fontSize: 9, color: '#ca8a04', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Coste/mes</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#fbbf24', letterSpacing: '-0.3px' }}>{server.coste_mensual}€</div>
                  {isLive && (
                    <div style={{ fontSize: 8, color: '#10b981', marginTop: 1 }}>● LIVE</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Specs grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, padding: '12px 16px', borderBottom: '1px solid rgba(77,130,188,0.12)' }}>
          {[
            { label: 'Plan', key: 'plan', icon: '⚡', value: server.plan },
            { label: 'RAM', key: 'ram', icon: '🧠', value: server.ram },
            { label: 'CPU', key: 'cpu', icon: '⚙️', value: server.cpu },
            { label: 'Disco', key: 'disco', icon: '💾', value: server.disco },
            { label: 'IP', key: 'ip', icon: '🌐', value: server.ip },
            { label: 'Coste/mes', key: 'coste_mensual', icon: '💶', value: server.coste_mensual + '€' },
            { label: 'Coolify URL', key: 'coolify_url', icon: '🔗', value: server.coolify_url || '—', span: true },
          ].map(({ label, key, icon, value, span }) => (
            <div key={key} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 7, padding: '7px 8px', border: '1px solid rgba(77,130,188,0.1)', gridColumn: span ? '1 / -1' : undefined }}>
              <div style={{ fontSize: 11, color: '#4d82bc', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 3 }}>{icon} {label}</div>
              {editing
                ? <input value={draft[key] || ''} onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))} style={{ ...fieldStyle, fontSize: 11, padding: '3px 6px' }} />
                : key === 'coolify_url' && value !== '—'
                  ? <a href={value} target="_blank" rel="noreferrer" style={{ fontSize: 14, fontWeight: 600, color: '#84b6f4', textDecoration: 'none', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value} ↗</a>
                  : <div style={{ fontSize: 14, fontWeight: 600, color: '#c8daf5', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
              }
            </div>
          ))}
        </div>

        {/* Live metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, padding: '10px 16px', borderBottom: '1px solid rgba(77,130,188,0.12)' }}>
          {/* CPU usage */}
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 7, padding: '8px 10px', border: '1px solid rgba(77,130,188,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 12, color: '#4d82bc', textTransform: 'uppercase', letterSpacing: 0.7 }}>⚡ CPU Usage</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: cpu > 80 ? '#ef4444' : cpu > 50 ? '#f59e0b' : '#10b981' }}>
                {isLive ? `${cpu.toFixed(1)}%` : '—'}
              </span>
            </div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: isLive ? `${Math.min(cpu,100)}%` : '0%', borderRadius: 2, transition: 'width 0.8s ease',
                background: cpu > 80 ? '#ef4444' : cpu > 50 ? '#f59e0b' : 'linear-gradient(90deg,#2563eb,#10b981)' }} />
            </div>
          </div>
          {/* Traffic out */}
          <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 7, padding: '8px 10px', border: '1px solid rgba(77,130,188,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <span style={{ fontSize: 12, color: '#4d82bc', textTransform: 'uppercase', letterSpacing: 0.7 }}>📡 Traffic Out</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#84b6f4' }}>
                {isLive ? formatBytes(trafficOut) : '—'}
              </span>
            </div>
            <div style={{ height: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: isLive ? `${trafficPct}%` : '0%', borderRadius: 2, transition: 'width 0.8s ease',
                background: 'linear-gradient(90deg,#2563eb,#84b6f4)' }} />
            </div>
            {isLive && <div style={{ fontSize: 11, color: '#4d82bc', marginTop: 3 }}>de {formatBytes(trafficIncluded)} incluidos</div>}
          </div>
        </div>

        {/* Apps list */}
        <div style={{ padding: '12px 16px 16px' }}>
          <div style={{ fontSize: 12, color: '#4d82bc', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, fontWeight: 600 }}>
            {server.apps.length} Contenedor{server.apps.length !== 1 ? 'es' : ''} · {server.apps.reduce((s, a) => s + (a.subApps?.length || 0), 0)} Aplicaciones
          </div>
          {(editing ? draft : server).apps.map((app, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                background: 'rgba(255,255,255,0.04)', borderRadius: 7,
                border: '1px solid rgba(77,130,188,0.1)',
                cursor: app.subApps?.length ? 'pointer' : 'default'
              }} onClick={() => !editing && app.subApps?.length && toggleExpand(i)}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: app.estado ? '#10b981' : '#6b7280', flexShrink: 0,
                  boxShadow: app.estado ? '0 0 5px #10b981' : 'none',
                  cursor: editing ? 'pointer' : 'default'
                }} onClick={e => { if (editing) { e.stopPropagation(); toggleAppStatus(i); } }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editing ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <input value={app.nombre} onChange={e => setDraft(d => ({ ...d, apps: d.apps.map((a, idx) => idx === i ? { ...a, nombre: e.target.value } : a) }))} style={{ ...fieldStyle, flex: '0 0 40%' }} placeholder="Nombre" />
                      <input value={app.url} onChange={e => setDraft(d => ({ ...d, apps: d.apps.map((a, idx) => idx === i ? { ...a, url: e.target.value } : a) }))} style={{ ...fieldStyle, flex: 1 }} placeholder="URL" />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#c8daf5' }}>{app.nombre}</div>
                        <a href={app.url.startsWith('http') ? app.url : `https://${app.url}`} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: '#4d82bc', textDecoration: 'none', wordBreak: 'break-all', display: 'block' }}>{app.url}</a>
                      </div>
                      {app.subApps?.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                          <span style={{ fontSize: 10, color: '#6b9fd4', background: 'rgba(77,130,188,0.15)', padding: '2px 6px', borderRadius: 10 }}>
                            {app.subApps.length} apps
                          </span>
                          <span style={{ fontSize: 10, color: '#4d82bc', transition: 'transform 0.2s', display: 'inline-block', transform: expandedApps[i] ? 'rotate(180deg)' : 'none' }}>▾</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {editing && (
                  <button onClick={() => removeApp(i)} style={{ background: 'rgba(239,68,68,0.15)', border: 'none', borderRadius: 4, color: '#ef4444', cursor: 'pointer', padding: '2px 6px', fontSize: 11, flexShrink: 0 }}>✕</button>
                )}
              </div>
              {/* Sub-apps */}
              {!editing && expandedApps[i] && app.subApps?.length > 0 && (
                <div style={{ marginLeft: 16, marginTop: 2, padding: '6px 10px', background: 'rgba(77,130,188,0.05)', borderRadius: '0 0 7px 7px', border: '1px solid rgba(77,130,188,0.08)', borderTop: 'none' }}>
                  {app.subApps.map((sub, si) => (
                    <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4d82bc', flexShrink: 0 }} />
                      <span style={{ fontSize: 11, color: '#8ab4d4' }}>{sub}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {editing && (
            addingApp ? (
              <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                <input value={newApp.nombre} onChange={e => setNewApp(n => ({ ...n, nombre: e.target.value }))} style={{ ...fieldStyle, flex: '0 0 35%' }} placeholder="Nombre" />
                <input value={newApp.url} onChange={e => setNewApp(n => ({ ...n, url: e.target.value }))} style={{ ...fieldStyle, flex: 1 }} placeholder="URL / puerto" />
                <button onClick={addApp} style={{ background: '#10b981', border: 'none', borderRadius: 6, color: 'white', cursor: 'pointer', padding: '0 10px', fontSize: 13 }}>+</button>
                <button onClick={() => setAddingApp(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, color: '#9ca3af', cursor: 'pointer', padding: '0 8px', fontSize: 13 }}>✕</button>
              </div>
            ) : (
              <button onClick={() => setAddingApp(true)} style={{
                width: '100%', marginTop: 6, padding: '6px', background: 'rgba(77,130,188,0.08)',
                border: '1px dashed rgba(77,130,188,0.3)', borderRadius: 6, color: '#4d82bc', cursor: 'pointer', fontSize: 12
              }}>+ Añadir servicio</button>
            )
          )}
        </div>

        {editing && (
          <div style={{ padding: '0 16px 14px' }}>
            <button onClick={cancel} style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#9ca3af', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// EXTERNAL SERVICES CARD
// ============================================================

const EXT_VERSION = 5;

const EXT_SERVICES_DEFAULTS = [
  { id: 'supabase', nombre: 'Supabase', plan: 'Pro', coste: '25.00', url: 'https://supabase.com', descripcion: '1 plan Pro · 3 proyectos', subItems: [
    { nombre: 'CRM',       url: 'https://supabase.com/dashboard/project/zkznupgyaorojxpydlly', descripcion: 'Auth + CRM + AutoLeads' },
    { nombre: 'Marketing', url: 'https://supabase.com/dashboard/project/vgraqyqwrdgsqdkpltou', descripcion: 'Costes + Marketing' },
    { nombre: 'C360',      url: 'https://supabase.com/dashboard/project/ohjlkorrqwjpncgtnpkc', descripcion: 'Customer Leads' },
  ]},
  { id: 'ia', nombre: 'Servicios IA', plan: 'Pay/use', coste: '~26.00', url: '', descripcion: 'OpenAI · Gemini · Claude · Pollinations', subItems: [
    { nombre: 'OpenAI',         url: 'https://platform.openai.com',    descripcion: 'GPT-4, DALL-E, Vision API' },
    { nombre: 'Google Gemini',  url: 'https://aistudio.google.com',    descripcion: 'Gemini Pro Vision' },
    { nombre: 'Anthropic',      url: 'https://console.anthropic.com',  descripcion: 'Claude API' },
    { nombre: 'Pollinations.ai',url: 'https://pollinations.ai',        descripcion: 'Generación imágenes free' },
  ]},
  { id: 'emailjs',   nombre: 'EmailJS',            plan: 'Free',    coste: '0.00',  url: 'https://emailjs.com',                    descripcion: '200 emails/mes' },
  { id: 'meta-ads',  nombre: 'Meta Ads API',        plan: 'Free',    coste: '0.00',  url: 'https://developers.facebook.com',        descripcion: 'MetaLeads + FBLeads' },
  { id: 'google-ads',nombre: 'Google Ads API',      plan: 'Free',    coste: '0.00',  url: 'https://ads.google.com',                 descripcion: 'Métricas campañas SEM' },
  { id: 'ga4',       nombre: 'GA4',                 plan: 'Free',    coste: '0.00',  url: 'https://analytics.google.com',           descripcion: 'Tráfico web Dimoauto' },
  { id: 'telegram',  nombre: 'Telegram Bot',        plan: 'Free',    coste: '0.00',  url: 'https://core.telegram.org/bots',         descripcion: 'Notif. comerciales' },
  { id: 'github',    nombre: 'GitHub',              plan: 'Free',    coste: '0.00',  url: 'https://github.com/grupodimoauto',       descripcion: 'Repos Dimoauto' },
  { id: 'cdnjs',     nombre: 'cdnjs (Cloudflare)',  plan: 'Free',    coste: '0.00',  url: 'https://cdnjs.cloudflare.com',           descripcion: 'jsPDF, librerías CDN' },
];

const ExternalServicesCard = () => {
  const [services, setServices] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('ext-services-v') || '0');
      if (v < EXT_VERSION) { localStorage.setItem('ext-services-v', String(EXT_VERSION)); return EXT_SERVICES_DEFAULTS; }
      const s = localStorage.getItem('ext-services'); return s ? JSON.parse(s) : EXT_SERVICES_DEFAULTS;
    } catch { return EXT_SERVICES_DEFAULTS; }
  });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(services);
  const [adding, setAdding] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [newSvc, setNewSvc] = useState({ nombre: '', plan: '', coste: '', url: '', descripcion: '' });

  const save = () => { setServices(draft); localStorage.setItem('ext-services', JSON.stringify(draft)); localStorage.setItem('ext-services-v', String(EXT_VERSION)); setEditing(false); };
  const cancel = () => { setDraft(services); setEditing(false); setAdding(false); };
  const addSvc = () => {
    if (!newSvc.nombre.trim()) return;
    setDraft(d => [...d, { ...newSvc, id: newSvc.nombre.toLowerCase() }]);
    setNewSvc({ nombre: '', plan: '', coste: '', url: '', descripcion: '' });
    setAdding(false);
  };
  const removeSvc = (i) => setDraft(d => d.filter((_, idx) => idx !== i));
  const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const totalEst = services.reduce((s, sv) => s + (parseFloat(sv.coste.replace('~','')) || 0), 0);
  const fieldStyle = { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 5, padding: '3px 7px', color: 'white', fontSize: 11, outline: 'none', width: '100%', boxSizing: 'border-box' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, height: '100%' }}>
      <div style={{ width: '100%', borderRadius: 16, overflow: 'hidden', background: 'linear-gradient(145deg, #0f1e35 0%, #0a1525 60%, #071020 100%)', border: '1px solid rgba(77,130,188,0.35)', boxShadow: '0 20px 60px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.06)', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ height: 3, background: 'linear-gradient(90deg, #2563eb, #4d82bc, #84b6f4, #4d82bc, #2563eb)' }} />

        {/* Header */}
        <div style={{ padding: '22px 20px 14px', borderBottom: '1px solid rgba(77,130,188,0.15)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div onClick={() => setEditing(true)} title="Clic para editar" style={{ fontSize: 18, fontWeight: 800, color: '#e8f1ff', letterSpacing: '-0.3px', cursor: 'pointer' }}>Servicios Externos</div>
              <div style={{ fontSize: 12, color: '#6b9fd4', marginTop: 3 }}>SaaS · APIs · Plataformas</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
              {editing ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={save} style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white' }}>✓ Guardar</button>
                  <button onClick={cancel} style={{ padding: '5px 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 11, background: 'rgba(255,255,255,0.08)', color: '#9ca3af' }}>✕</button>
                </div>
              ) : (
                <div style={{ background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 8, padding: '6px 12px', textAlign: 'right' }}>
                  <div style={{ fontSize: 9, color: '#ca8a04', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>Coste/mes</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: '#fbbf24', letterSpacing: '-0.3px' }}>~{totalEst.toFixed(2)}€</div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Services list */}
        <div style={{ padding: '12px 16px 16px', flex: 1 }}>
          <div style={{ fontSize: 12, color: '#4d82bc', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, fontWeight: 600 }}>
            {services.length} Servicios
          </div>
          {(editing ? draft : services).map((svc, i) => (
            <div key={svc.id || i} style={{ marginBottom: 4 }}>
              {/* Main row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: svc.subItems?.length && expanded[svc.id] ? '7px 7px 0 0' : 7, border: '1px solid rgba(77,130,188,0.1)', cursor: svc.subItems?.length && !editing ? 'pointer' : 'default' }}
                onClick={() => !editing && svc.subItems?.length && toggleExpand(svc.id)}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', flexShrink: 0, display: 'inline-block', boxShadow: '0 0 6px #10b981' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editing ? (
                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <input value={svc.nombre} onChange={e => setDraft(d => d.map((s,idx) => idx===i ? {...s, nombre: e.target.value} : s))} style={{...fieldStyle, flex: '0 0 30%'}} placeholder="Nombre" />
                      <input value={svc.plan} onChange={e => setDraft(d => d.map((s,idx) => idx===i ? {...s, plan: e.target.value} : s))} style={{...fieldStyle, flex: '0 0 20%'}} placeholder="Plan" />
                      <input value={svc.coste} onChange={e => setDraft(d => d.map((s,idx) => idx===i ? {...s, coste: e.target.value} : s))} style={{...fieldStyle, flex: '0 0 16%'}} placeholder="€" />
                      <input value={svc.descripcion} onChange={e => setDraft(d => d.map((s,idx) => idx===i ? {...s, descripcion: e.target.value} : s))} style={{...fieldStyle, flex: 1}} placeholder="Descripción" />
                    </div>
                  ) : (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#e8f1ff' }}>{svc.nombre}</span>
                          <span style={{ fontSize: 9, color: '#6b9fd4', background: 'rgba(77,130,188,0.15)', padding: '1px 5px', borderRadius: 8 }}>{svc.plan}</span>
                          {svc.subItems?.length > 0 && (
                            <span style={{ fontSize: 9, color: '#6b9fd4', background: 'rgba(77,130,188,0.1)', padding: '1px 5px', borderRadius: 8 }}>
                              {svc.subItems.length} ▾
                            </span>
                          )}
                        </div>
                        {svc.url
                          ? <a href={svc.url} target="_blank" rel="noreferrer" style={{ fontSize: 9, color: '#4d82bc', textDecoration: 'none', display: 'block', lineHeight: '1.2' }}>{svc.descripcion}</a>
                          : <span style={{ fontSize: 9, color: '#4d82bc', display: 'block', lineHeight: '1.2' }}>{svc.descripcion}</span>
                        }
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: parseFloat(svc.coste) > 0 ? '#84b6f4' : '#6ee7b7', flexShrink: 0, marginLeft: 8 }}>
                        {parseFloat(svc.coste) > 0 ? `${svc.coste}€` : 'Gratis'}
                      </span>
                    </div>
                  )}
                </div>
                {editing && <button onClick={() => removeSvc(i)} style={{ background: 'rgba(239,68,68,0.15)', border: 'none', borderRadius: 4, color: '#ef4444', cursor: 'pointer', padding: '2px 6px', fontSize: 10, flexShrink: 0 }}>✕</button>}
              </div>
              {/* Sub-items */}
              {!editing && expanded[svc.id] && svc.subItems?.length > 0 && (
                <div style={{ marginLeft: 16, padding: '6px 10px', background: 'rgba(77,130,188,0.05)', borderRadius: '0 0 7px 7px', border: '1px solid rgba(77,130,188,0.08)', borderTop: 'none' }}>
                  {svc.subItems.map((sub, si) => (
                    <div key={si} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: si < svc.subItems.length - 1 ? '1px solid rgba(77,130,188,0.07)' : 'none' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#4d82bc', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: '#c8daf5' }}>{sub.nombre}</span>
                        {sub.url && <a href={sub.url} target="_blank" rel="noreferrer" style={{ fontSize: 10, color: '#4d82bc', textDecoration: 'none', marginLeft: 6 }}>{sub.descripcion}</a>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {editing && (
            adding ? (
              <div style={{ display: 'flex', gap: 3, marginTop: 6, flexWrap: 'wrap' }}>
                <input value={newSvc.nombre} onChange={e => setNewSvc(n => ({...n, nombre: e.target.value}))} style={{...fieldStyle, flex: '0 0 30%'}} placeholder="Nombre" />
                <input value={newSvc.plan} onChange={e => setNewSvc(n => ({...n, plan: e.target.value}))} style={{...fieldStyle, flex: '0 0 18%'}} placeholder="Plan" />
                <input value={newSvc.coste} onChange={e => setNewSvc(n => ({...n, coste: e.target.value}))} style={{...fieldStyle, flex: '0 0 14%'}} placeholder="€" />
                <input value={newSvc.descripcion} onChange={e => setNewSvc(n => ({...n, descripcion: e.target.value}))} style={{...fieldStyle, flex: 1}} placeholder="Descripción" />
                <button onClick={addSvc} style={{ background: '#10b981', border: 'none', borderRadius: 6, color: 'white', cursor: 'pointer', padding: '0 10px', fontSize: 12 }}>+</button>
                <button onClick={() => setAdding(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 6, color: '#9ca3af', cursor: 'pointer', padding: '0 8px', fontSize: 12 }}>✕</button>
              </div>
            ) : (
              <button onClick={() => setAdding(true)} style={{ width: '100%', marginTop: 6, padding: '6px', background: 'rgba(77,130,188,0.08)', border: '1px dashed rgba(77,130,188,0.3)', borderRadius: 6, color: '#4d82bc', cursor: 'pointer', fontSize: 11 }}>+ Añadir servicio</button>
            )
          )}
        </div>

        {editing && (
          <div style={{ padding: '0 16px 14px' }}>
            <button onClick={cancel} style={{ width: '100%', padding: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#9ca3af', cursor: 'pointer', fontSize: 12 }}>Cancelar</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// ANIMATED LOGIN BACKGROUND (Canvas)
// ============================================================
const AnimatedLoginBackground = React.forwardRef(({ cardRect }, ref) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const stateRef = useRef({ nodes: [], clickBoost: 0 });
  
  React.useImperativeHandle(ref, () => ({
    triggerPulse: () => { stateRef.current.clickBoost = 1; }
  }));
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let width, height;
    const numNodes = 80;
    const connectionDistance = 180;
    const nodeSpeed = 0.3;
    
    const createNodes = () => {
      const nodes = [];
      for (let i = 0; i < numNodes; i++) {
        nodes.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * nodeSpeed,
          vy: (Math.random() - 0.5) * nodeSpeed,
          radius: 2 + Math.random() * 2,
          baseRadius: 2 + Math.random() * 2
        });
      }
      return nodes;
    };
    
    const setup = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (stateRef.current.nodes.length === 0) {
        stateRef.current.nodes = createNodes();
      }
    };
    
    setup();
    window.addEventListener('resize', setup);
    
    const animate = () => {
      const state = stateRef.current;
      const nodes = state.nodes;
      
      if (state.clickBoost > 0) state.clickBoost = Math.max(0, state.clickBoost - 0.015);
      
      const bgGradient = ctx.createLinearGradient(0, 0, width, height);
      bgGradient.addColorStop(0, '#0a1628');
      bgGradient.addColorStop(0.5, '#0d1f3c');
      bgGradient.addColorStop(1, '#081430');
      ctx.fillStyle = bgGradient;
      ctx.fillRect(0, 0, width, height);
      
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0) { node.x = 0; node.vx *= -1; }
        if (node.x > width) { node.x = width; node.vx *= -1; }
        if (node.y < 0) { node.y = 0; node.vy *= -1; }
        if (node.y > height) { node.y = height; node.vy *= -1; }
        node.radius = node.baseRadius * (1 + state.clickBoost * 0.5);
      }
      
      ctx.lineWidth = 1;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < connectionDistance) {
            const opacity = (1 - distance / connectionDistance) * 0.6;
            const boostedOpacity = Math.min(1, opacity + state.clickBoost * 0.3);
            const gradient = ctx.createLinearGradient(nodes[i].x, nodes[i].y, nodes[j].x, nodes[j].y);
            gradient.addColorStop(0, `rgba(77, 130, 188, ${boostedOpacity})`);
            gradient.addColorStop(0.5, `rgba(132, 182, 244, ${boostedOpacity * 0.8})`);
            gradient.addColorStop(1, `rgba(77, 130, 188, ${boostedOpacity})`);
            ctx.strokeStyle = gradient;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      
      for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        const glowRadius = node.radius * 4;
        const glowGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, glowRadius);
        const glowIntensity = 0.15 + state.clickBoost * 0.2;
        glowGradient.addColorStop(0, `rgba(132, 182, 244, ${glowIntensity})`);
        glowGradient.addColorStop(0.5, `rgba(77, 130, 188, ${glowIntensity * 0.3})`);
        glowGradient.addColorStop(1, 'rgba(77, 130, 188, 0)');
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, glowRadius, 0, Math.PI * 2);
        ctx.fill();
        
        const nodeGradient = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.radius);
        nodeGradient.addColorStop(0, `rgba(255, 255, 255, ${0.9 + state.clickBoost * 0.1})`);
        nodeGradient.addColorStop(0.3, `rgba(196, 218, 250, ${0.8 + state.clickBoost * 0.1})`);
        nodeGradient.addColorStop(1, `rgba(77, 130, 188, 0.6)`);
        ctx.fillStyle = nodeGradient;
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fill();
      }
      
      if (cardRect) {
        const cardCenterX = cardRect.x + cardRect.width / 2;
        const cardCenterY = cardRect.y + cardRect.height / 2;
        const glowRadius = Math.max(cardRect.width, cardRect.height) * 0.8;
        const cardGlow = ctx.createRadialGradient(cardCenterX, cardCenterY, 0, cardCenterX, cardCenterY, glowRadius);
        const intensity = 0.1 + state.clickBoost * 0.2;
        cardGlow.addColorStop(0, `rgba(0, 81, 135, ${intensity})`);
        cardGlow.addColorStop(0.5, `rgba(77, 130, 188, ${intensity * 0.3})`);
        cardGlow.addColorStop(1, 'rgba(0, 81, 135, 0)');
        ctx.fillStyle = cardGlow;
        ctx.fillRect(0, 0, width, height);
      }
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    stateRef.current.nodes = createNodes();
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      window.removeEventListener('resize', setup);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [cardRect]);
  
  return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />;
});

// ============================================================
// LOGIN SCREEN
// ============================================================
const LoginScreen = ({ onLogin, isLoading, error }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardRect, setCardRect] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const cardRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const savedEmail = localStorage.getItem('costes-remember-email');
    const savedPassword = localStorage.getItem('costes-remember-password');
    const savedRemember = localStorage.getItem('costes-remember-me');
    if (savedRemember === 'true' && savedEmail && savedPassword) {
      setEmail(savedEmail);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    const updateCardRect = () => {
      if (cardRef.current) {
        const rect = cardRef.current.getBoundingClientRect();
        setCardRect({ x: rect.left, y: rect.top, width: rect.width, height: rect.height });
      }
    };
    updateCardRect();
    window.addEventListener('resize', updateCardRect);
    const timer = setTimeout(updateCardRect, 100);
    return () => { window.removeEventListener('resize', updateCardRect); clearTimeout(timer); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setIsSubmitting(true);
    if (canvasRef.current?.triggerPulse) canvasRef.current.triggerPulse();

    const { data, error: authError } = await supabaseAuth.signIn(email, password);
    
    if (data && !authError) {
      if (rememberMe) {
        localStorage.setItem('costes-remember-email', email);
        localStorage.setItem('costes-remember-password', password);
        localStorage.setItem('costes-remember-me', 'true');
      } else {
        localStorage.removeItem('costes-remember-email');
        localStorage.removeItem('costes-remember-password');
        localStorage.removeItem('costes-remember-me');
      }
      onLogin({ email });
    } else {
      setLoginError(authError || 'Email o contraseña incorrectos');
    }
    setIsSubmitting(false);
  };

  const inputStyle = {
    width: '100%', padding: '14px 16px', borderRadius: '8px', border: '1px solid #e0e0e0',
    fontSize: '14px', background: '#f9fafb', color: '#1a1a1a', outline: 'none', boxSizing: 'border-box'
  };
  const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 500, color: '#666', marginBottom: '8px' };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', padding: '20px', position: 'relative', overflow: 'hidden' }}>
      <AnimatedLoginBackground ref={canvasRef} cardRect={cardRect} />
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      
      <div ref={cardRef} style={{
        width: '100%', maxWidth: '420px', background: 'rgba(255, 255, 255, 0.98)', borderRadius: '16px',
        padding: '40px', boxShadow: '0 25px 80px rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)',
        position: 'relative', zIndex: 1, backdropFilter: 'blur(10px)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '35px' }}>
          <div style={{ width: '200px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
            <img src="/logotipo.png" alt="Dimoauto" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </div>
          <h1 style={{ color: '#1a1a1a', fontSize: '24px', fontWeight: 700, margin: '0 0 8px 0' }}>Control de Costes</h1>
          <p style={{ color: '#555', fontSize: '14px', margin: 0 }}>Grupo Dimoauto</p>
        </div>

        {error && (
          <div style={{ background: 'rgba(220, 53, 69, 0.1)', border: '1px solid #DC3545', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px' }}>
            <span style={{ color: '#DC3545', fontSize: '13px' }}>⚠️ {error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="usuario@dimoauto.com" required style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#005187'} onBlur={(e) => e.target.style.borderColor = '#e0e0e0'} />
          </div>
          <div style={{ marginBottom: '25px' }}>
            <label style={labelStyle}>Contraseña</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required style={inputStyle}
              onFocus={(e) => e.target.style.borderColor = '#005187'} onBlur={(e) => e.target.style.borderColor = '#e0e0e0'} />
          </div>

          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#666' }}>
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{ cursor: 'pointer' }} />
              Recordar credenciales
            </label>
          </div>

          {loginError && (
            <div style={{ background: 'rgba(220, 53, 69, 0.1)', border: '1px solid #DC3545', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', textAlign: 'center' }}>
              <span style={{ color: '#DC3545', fontSize: '13px' }}>❌ {loginError}</span>
            </div>
          )}

          <button type="submit" disabled={isSubmitting || isLoading} style={{
            width: '100%', padding: '14px', background: isSubmitting ? '#9CA3AF' : 'linear-gradient(135deg, #005187, #4d82bc)',
            border: 'none', borderRadius: '8px', color: 'white', fontSize: '15px', fontWeight: 600,
            cursor: isSubmitting ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
          }}>
            {isSubmitting ? (
              <>
                <span style={{ width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></span>
                Verificando...
              </>
            ) : (<>🔐 Iniciar Sesión</>)}
          </button>
        </form>

        <div style={{ marginTop: '24px', padding: '12px', background: '#F0FDF4', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>🔒</span>
          <span style={{ fontSize: '12px', color: '#166534' }}>Conexión segura con autenticación cifrada</span>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// BARRA DE PROGRESO
// ============================================================
const ProgressBar = ({ value, max, color }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const isWarning = percentage > 80;
  const isOver = value > max;
  
  return (
    <div style={{ width: '100%' }}>
      <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(percentage, 100)}%`, background: isOver ? '#EF4444' : isWarning ? '#F59E0B' : color, borderRadius: 4 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: COLORS.gray }}>
        <span>{value.toLocaleString('es-ES')}</span>
        <span>de {max.toLocaleString('es-ES')}</span>
      </div>
    </div>
  );
};

// ============================================================
// HEADER
// ============================================================
const Header = ({ user, onLogout, onOpenToken, tokensReady }) => (
  <header style={{
    background: COLORS.white,
    padding: '12px 24px', 
    borderBottom: `3px solid ${COLORS.primary}`,
    display: 'flex', 
    justifyContent: 'space-between', 
    alignItems: 'center'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <img src="/logotipo.png" alt="Dimoauto" style={{ height: 40 }} onError={(e) => e.target.style.display = 'none'} />
      <div>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: COLORS.dark }}>Control de Costes</h1>
      </div>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <button onClick={onOpenToken} style={{
        padding: '5px 11px', border: '1px solid #cbd5e1', borderRadius: 7, background: 'transparent',
        cursor: 'pointer', fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 5
      }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: tokensReady ? '#10b981' : '#f59e0b', display: 'inline-block', boxShadow: tokensReady ? '0 0 5px #10b981' : '0 0 5px #f59e0b' }} />
        {tokensReady ? 'API conectada' : 'Hetzner API'}
      </button>
      <span style={{ fontSize: 13, color: COLORS.gray }}>{user?.email}</span>
      <button onClick={onLogout} style={{
        padding: '8px 16px', background: COLORS.primary, border: 'none',
        borderRadius: 6, color: 'white', fontSize: 13, cursor: 'pointer'
      }}>Cerrar sesión</button>
    </div>
  </header>
);

// ============================================================
// COMPONENTE PRINCIPAL
// ============================================================
const CostesControlApp = () => {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  
  // eslint-disable-next-line no-unused-vars
  const [hubs, setHubs] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [apps, setApps] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [resumen, setResumen] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [limites, setLimites] = useState([]);
  const [servers, setServers] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('costes-servers-v') || '0');
      if (v < SERVERS_VERSION) {
        localStorage.setItem('costes-servers-v', String(SERVERS_VERSION));
        localStorage.removeItem('costes-servers');
        return SERVER_DEFAULTS;
      }
      const s = localStorage.getItem('costes-servers');
      return s ? JSON.parse(s) : SERVER_DEFAULTS;
    } catch { return SERVER_DEFAULTS; }
  });
  const updateServer = (updated) => {
    const next = servers.map(s => s.id === updated.id ? updated : s);
    setServers(next);
    localStorage.setItem('costes-servers', JSON.stringify(next));
    localStorage.setItem('costes-servers-v', String(SERVERS_VERSION));
  };

  // Hetzner live metrics — tokens per server (env var > localStorage)
  const getToken = (serverId) =>
    serverId === 'server1'
      ? (process.env.REACT_APP_HETZNER_TOKEN_S1 || localStorage.getItem('hetzner-token-s1') || '')
      : (process.env.REACT_APP_HETZNER_TOKEN_S2 || localStorage.getItem('hetzner-token-s2') || '');

  const [showTokenInput, setShowTokenInput] = useState(false);
  const [liveMetrics, setLiveMetrics] = useState({});

  const fetchHetznerMetrics = useCallback(async (serverList) => {
    const results = {};
    await Promise.all(serverList.map(async (srv) => {
      if (!srv.hetzner_id) return;
      const token = getToken(srv.id);
      if (!token) return;
      try {
        const detailRes = await fetch(`https://api.hetzner.cloud/v1/servers/${srv.hetzner_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!detailRes.ok) { results[srv.id] = { error: true }; return; }
        const { server: hServer } = await detailRes.json();

        const now = new Date();
        const start = new Date(now - 5 * 60 * 1000).toISOString();
        const end = now.toISOString();
        const metricRes = await fetch(
          `https://api.hetzner.cloud/v1/servers/${srv.hetzner_id}/metrics?type=cpu&start=${start}&end=${end}&step=60`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        let cpuUsage = 0;
        if (metricRes.ok) {
          const { metrics } = await metricRes.json();
          const cpuValues = metrics?.time_series?.cpu?.values || [];
          if (cpuValues.length) {
            const vals = cpuValues.map(v => parseFloat(v[1])).filter(v => !isNaN(v));
            cpuUsage = vals.reduce((a, b) => a + b, 0) / (vals.length || 1);
          }
        }
        results[srv.id] = {
          cpu_usage: cpuUsage,
          outgoing_traffic: hServer.outgoing_traffic,
          included_traffic: hServer.included_traffic,
          status: hServer.status
        };
      } catch { results[srv.id] = { error: true }; }
    }));
    setLiveMetrics(results);
  }, []);

  // tokens configured = at least one is set
  const tokensReady = servers.some(s => getToken(s.id));

  useEffect(() => {
    fetchHetznerMetrics(servers);
    const interval = setInterval(() => fetchHetznerMetrics(servers), 60000);
    return () => clearInterval(interval);
  }, [servers, fetchHetznerMetrics]);

  // Check auth on mount
  useEffect(() => {
    const currentUser = supabaseAuth.getCurrentUser();
    if (currentUser) setUser(currentUser);
    setAuthLoading(false);
  }, []);


  const handleLogin = (userData) => setUser(userData);
  const handleLogout = () => { supabaseAuth.signOut(); setUser(null); };

  if (authLoading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a' }}>
      <div style={{ color: 'white', fontSize: 16 }}>Cargando...</div>
    </div>
  );

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: COLORS.background }}>
      <div style={{ fontSize: 16, color: COLORS.primary }}>Cargando datos...</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #eef2f8 0%, #e8eef6 50%, #dfe8f3 100%)', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <Header user={user} onLogout={handleLogout} onOpenToken={() => setShowTokenInput(v => !v)} tokensReady={tokensReady} />

      {/* Token input dropdown — anchored under header */}
      {showTokenInput && (
        <div style={{ position: 'fixed', top: 70, right: 20, zIndex: 100, background: 'white', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column', gap: 8, minWidth: 320 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 2 }}>Tokens API Hetzner (read-only)</div>
          {[{ id: 'server1', label: 'Servidor 1 - Backend', key: 'hetzner-token-s1' }, { id: 'server2', label: 'Servidor 2 - Frontend', key: 'hetzner-token-s2' }].map(({ id, label, key }) => (
            <div key={id} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: 11, color: '#94a3b8', width: 140, flexShrink: 0 }}>{label}</span>
              <input type="password" placeholder="Token..." defaultValue={localStorage.getItem(key) || ''}
                onBlur={e => { const t = e.target.value.trim(); if (t) localStorage.setItem(key, t); else localStorage.removeItem(key); }}
                style={{ flex: 1, padding: '5px 9px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 11, outline: 'none' }} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
            <button onClick={() => { setShowTokenInput(false); fetchHetznerMetrics(servers); }} style={{ flex: 1, padding: '6px', background: COLORS.primary, border: 'none', borderRadius: 6, color: 'white', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>Guardar y conectar</button>
            <button onClick={() => setShowTokenInput(false)} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 11, color: '#64748b' }}>✕</button>
          </div>
        </div>
      )}

      <main style={{ maxWidth: 1400, margin: '0 auto', padding: '12px 20px 20px' }}>

        {/* Server Cards + External Services */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 24, alignItems: 'stretch' }}>
          {servers.map(server => (
            <ServerCard key={server.id} server={server} onSave={updateServer} liveMetrics={liveMetrics[server.id]} />
          ))}
          <ExternalServicesCard />
        </div>

        {/* Botón actualizar */}
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <button onClick={() => fetchHetznerMetrics(servers)} style={{
            padding: '9px 22px', background: COLORS.primary, color: 'white', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer', opacity: tokensReady ? 1 : 0.5
          }}>↻ Actualizar métricas</button>
        </div>
      </main>
    </div>
  );
};

export default CostesControlApp;
